import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AppLayout from '../layout/AppLayout';
import { Spinner } from '../ui/Spinner';

const getDashboardPath = (role) => {
  switch (role) {
    case 'admin':
      return '/admin/dashboard';
    case 'manager':
      return '/manager/dashboard';
    case 'instructor':
      return '/instructor/dashboard';
    case 'demonstrator':
      return '/demonstrator/dashboard';
    case 'staff':
      return '/staff/dashboard';
    case 'student':
      return '/student/dashboard';
    default:
      return '/dashboard';
  }
};

const ProtectedRoute = ({
  children,
  allowedRoles,
  requireVerification = true,
  showLayout = true,
}) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Check if the route is a public route
  const publicRoutes = [
    '/',
    '/gallery',
    '/services',
    '/contact',
    '/academy',
    '/store',
    '/about',
    '/verification',
  ];
  if (publicRoutes.includes(location.pathname)) {
    return children;
  }

  if (!currentUser) {
    // Not logged in, redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (
    requireVerification &&
    !['admin', 'manager', 'student'].includes(currentUser.role) &&
    !currentUser.emailVerified
  ) {
    return <Navigate to="/verify-email" />;
  }

  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    // User's role is not authorized, redirect to their dashboard
    return <Navigate to={getDashboardPath(currentUser.role)} />;
  }

  if (!showLayout) {
    return children;
  }

  return (
    <AppLayout>
      {typeof children === 'function' ? children({ currentUser }) : children}
    </AppLayout>
  );
};

export default ProtectedRoute;
